export type AccordanceAiField = 'accordance' | 'critical_issue';

type FieldMetrics = {
  reviewClicks: number;
  acceptedClicks: number;
  discardClicks: number;
  errors: number;
  firstReviewedTextLength?: number;
};

export type AccordanceAiUseProperties = {
  project_id: number;
  is_saved: boolean;
  is_draft: boolean;
  is_edited: boolean;
  is_sent_for_approval: boolean;
  is_reviewed_clicked: boolean;
  count_ai_review_click_accordance: number;
  count_ai_review_click_critical_issue: number;
  count_ai_accepted_click_accordance: number;
  count_ai_accepted_click_critical_issue: number;
  count_ai_discard_accordance: number;
  count_ai_discard_critical_issue: number;
  text_length_accordance: number;
  text_length_critical_issue: number;
  count_ai_error_accordance: number;
  count_ai_error_critical_issue: number;
};

const createFieldMetrics = (): FieldMetrics => ({
  reviewClicks: 0,
  acceptedClicks: 0,
  discardClicks: 0,
  errors: 0,
});

export const createAccordanceAiMetrics = () => {
  const metrics: Record<AccordanceAiField, FieldMetrics> = {
    accordance: createFieldMetrics(),
    critical_issue: createFieldMetrics(),
  };

  return {
    recordReviewClick(field: AccordanceAiField, originalText: string) {
      const fieldMetrics = metrics[field];
      fieldMetrics.reviewClicks += 1;
      if (fieldMetrics.firstReviewedTextLength === undefined) {
        fieldMetrics.firstReviewedTextLength = originalText.length;
      }
    },
    recordAcceptedClick(field: AccordanceAiField) {
      metrics[field].acceptedClicks += 1;
    },
    recordDiscardClick(field: AccordanceAiField) {
      metrics[field].discardClicks += 1;
    },
    recordError(field: AccordanceAiField) {
      metrics[field].errors += 1;
    },
    buildProperties({
      projectId,
      isSaved,
      isDraft,
      isEdited,
      isSentForApproval,
      accordanceText,
      criticalIssueText,
    }: {
      projectId: number;
      isSaved: boolean;
      isDraft: boolean;
      isEdited: boolean;
      isSentForApproval: boolean;
      accordanceText: string;
      criticalIssueText: string;
    }): AccordanceAiUseProperties {
      const accordance = metrics.accordance;
      const criticalIssue = metrics.critical_issue;

      return {
        project_id: projectId,
        is_saved: isSaved,
        is_draft: isDraft,
        is_edited: isEdited,
        is_sent_for_approval: isSentForApproval,
        is_reviewed_clicked: accordance.reviewClicks > 0 || criticalIssue.reviewClicks > 0,
        count_ai_review_click_accordance: accordance.reviewClicks,
        count_ai_review_click_critical_issue: criticalIssue.reviewClicks,
        count_ai_accepted_click_accordance: accordance.acceptedClicks,
        count_ai_accepted_click_critical_issue: criticalIssue.acceptedClicks,
        count_ai_discard_accordance: accordance.discardClicks,
        count_ai_discard_critical_issue: criticalIssue.discardClicks,
        text_length_accordance: accordance.firstReviewedTextLength ?? accordanceText.length,
        text_length_critical_issue: criticalIssue.firstReviewedTextLength ?? criticalIssueText.length,
        count_ai_error_accordance: accordance.errors,
        count_ai_error_critical_issue: criticalIssue.errors,
      };
    },
  };
};

export type AccordanceAiMetrics = ReturnType<typeof createAccordanceAiMetrics>;
